import React from "react"
import { useAccessContext } from "../component/guard/AccessGuard"
import { api, auth, firestore } from "../core"
import * as FirebaseAuth from "firebase/auth"
import { FollowingUserList } from "../component/user/FollowingUserList"
import { FollowUserList } from "../component/user/FollowUserList"
import { BlockingUserList } from "../component/user/BlockingUserList"
import { UserInventoryDocument } from "popopo-msg"
import { BlockUserForm } from "../component/user/BlockUserForm"
import { UserProfileForm } from "../component/user/UserProfileForm"
import { SpaceList } from "../component/live/SpaceList"
import { InviteForm } from "../component/user/InviteForm"
import { SpaceCreateForm } from "../component/space/SpaceCreateForm"
import { UserProfileIconForm } from "../component/user/UserProfileIconForm"
import { UserInfo } from "../component/user/UserInfo"
import { UserProfileFriendCodeForm } from "../component/user/UserProfileFriendCodeForm"
import { UserLookForm } from "../component/user/UserLookForm"
import { UserCoinBalanceList } from "../component/user/UserCoinBalanceList"
import { VirtualCastOAuthAPIForm } from "../component/oauth/VirtualCastOAuthAPIForm"
import { VirtualCastOAuthAccessTokenDeleteForm } from "../component/oauth/VirtualCastOAuthAccessTokenDeleteForm"
import { VirtualCastOAuthAuthCodeForm } from "../component/oauth/VirtualCastOAuthAuthCodeForm"
import { VrmRegistrationDebugForm } from "../component/user/VrmRegistrationDebugForm"
import { InitialLookDebugForm } from "../component/user/InitialLookDebugForm"
import { UserDeviceList } from "../component/user/UserDeviceList"
import { UserDeviceRegisterForm } from "../component/user/UserDeviceRegisterForm"
import { UserDeviceDebug } from "../component/user/UserDeviceDebug"
import { useLatestFriendInvite, usePushSettings, useUserInventories, useUserDevices, useUserPrivate } from "popopo-lib-front/firestore-access"
import { UserStampCards } from "../component/user/UserStampCards"
import { UserPushSettingForm } from "../component/user/UserPushSettingForm"
import { Spinner } from "react-bootstrap"
import { CreateInviteForm } from "../component/CreateInviteForm"
import { useToast } from "popopo-lib-front/hook"
import { ShareImageDownloadButton } from "../component/ShareImageDownloadButton"

export const My = () => {
  const context = useAccessContext()
  const { credential, user } = context
  const pushSettings = usePushSettings(firestore, user.id)
  const toast = useToast()
  const invite = useLatestFriendInvite(firestore, user.id)
  const userInventories = useUserInventories(firestore, user.id)
  const userDevices = useUserDevices(firestore, user.id, 100)
  const userPrivate = useUserPrivate(firestore, user.id)

  return (<>
    <div className="row gy-5 justify-content-center">
      <div className="col-12 col-xl-6 d-flex flex-column align-items-center gap-3">
        <h3>User Info</h3>
        <UserInfo user={context.user} />
        <ShareImageDownloadButton
          getIdToken={() => credential.user.getIdToken()}
          path="/api/v2/users/me/share-image"
          filename="profile-share-image.png"
          label="プロフィール共有画像DL"
        />

        <h3>コイン残高</h3>
        {userPrivate === "loading" ? (
          <Spinner />
        ) : userPrivate === null ? (
          <p>コイン残高情報を取得できませんでした</p>
        ) : (
          <UserCoinBalanceList coinBalances={userPrivate.coin_balance_list ?? []} />
        )}

        <h3>Regenerate friend code</h3>
        <UserProfileFriendCodeForm firebase={credential.user} user={user} />

        <h3>保有アイテム</h3>
        {userInventories === "loading" ? (
          <Spinner />
        ) : userInventories.length === 0 ? (
          <p>保有アイテムはありません</p>
        ) : (
          <div className="row row-cols-1 row-cols-md-2 g-4 w-100">
            {userInventories.map((inventory: UserInventoryDocument) => (
              <div className="col" key={inventory.id}>
                <div className="card h-100">
                  <img src={inventory.item.media.first.value} className="card-img-top" alt={inventory.item.name} />
                  <div className="card-body">
                    <h5 className="card-title">{inventory.item.name}</h5>
                    <p className="card-text">{inventory.item.description}</p>
                    {inventory.reason === "purchase" && inventory.sale && (
                      <p className="card-text"><small className="text-muted">セール適用: {inventory.sale.name} ({inventory.sale.percentage}% OFF)</small></p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <h3>スタンプカード</h3>
        <UserStampCards userId={user.id} />

        <h3>Spaces</h3>
        <SpaceList firebase={credential.user} />

        <h3>Create Space</h3>
        <SpaceCreateForm firebase={credential.user} />
      </div>
    </div>

    <div className="row mt-5 gy-5 justify-content-center">
      <div className="col-12 col-xl-6 d-flex flex-column align-items-center gap-3">
        <h3>Update My Profile</h3>
        <UserProfileForm firebase={credential.user} user={user} />

        <h3>Update Icon</h3>
        <UserProfileIconForm firebase={credential.user} user={user} />

        <h3>Change look</h3>
        <UserLookForm firebase={credential.user} user={user} />

        <h3>Virtual Cast OAuth</h3>
        {user.connection?.virtualcast === true ? (
          <>
            <VirtualCastOAuthAPIForm firebase={credential.user} user={user} />
            <VirtualCastOAuthAccessTokenDeleteForm firebase={credential.user} />
          </>
        ) : (
          <VirtualCastOAuthAuthCodeForm firebase={credential.user} user={user} />
        )}

        <h3>InitialLook 設定 (デバッグ)</h3>
        <InitialLookDebugForm firebase={credential.user} user={user} />

        <h3>VC VRM 登録 (デバッグ)</h3>
        <VrmRegistrationDebugForm firebase={credential.user} user={user} />
      </div>

      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Friend Invite</h3>
        {invite === "loading" ? <Spinner /> : invite === null ? <p>招待コードなし</p>: <div>
          <p>({invite.status}) <code>{invite.id}</code> {<button onClick={async () => {
            const idToken = await context.credential.user.getIdToken()
            const result = await api.DELETE("/api/v2/users/me/friend-invites/{invite_key}", {
              params: { path: { invite_key: invite.id } },
              headers: {
                authorization: `Bearer ${idToken}`,
              },
            })
            if (result.error) {
              toast.add({ type: "danger", title: "Invite Delete", message: result.error?.message })
            } else {
              toast.add({ type: "success", title: "Invite Delete", message: "Invite Delete" })
            }
          }} className="btn btn-danger btn-sm">削除</button> }</p>
          <p>回数: {invite.count} / {invite.limit}</p>
          <p>有効時間:{new Date(invite.created_at).toLocaleString()} から {invite.expired_at_seconds}秒</p>
          <ShareImageDownloadButton
            getIdToken={() => credential.user.getIdToken()}
            path={`/api/v2/users/me/friend-invites/${invite.id}/share-image`}
            filename={`friend-invite-${invite.id}-share-image.png`}
            label="友だち招待共有画像DL"
          />
        </div>}
        <CreateInviteForm
          onCreate={async (request) => {
            try {
              const idToken = await context.credential.user.getIdToken()
              const result = await api.POST("/api/v2/users/me/friend-invites", {
                headers: {
                  authorization: `Bearer ${idToken}`,
                },
                body: request
              })
              if (!result.error) {
                return { error: null, createdInviteLink: result.data.inviteLink, createdInviteKey: result.data.inviteKey }
              } else {
                return { error: result.error?.kind + ": " + result.error?.message }
              }
            } catch (e) {
              return { error: e?.toString() }
            }
          }}
        />
        <h3>Use Friend Invite</h3>
        <InviteForm onSubmitJoin={
          async (inviteKey) => {
            try {
              const idToken = await context.credential.user.getIdToken()
              const response = await api.POST("/api/v2/users/me/use-friend-invites/{invite_key}", {
                params: { path: { invite_key: inviteKey } },
                headers: {
                  authorization: `Bearer ${idToken}`,
                },
              })
              if (response.data) {
                toast.add({ type: "success", title: "Friend Invite", message: `${response.data.userId}と友だちになりました` })
              } else {
                toast.add({ type: "danger", title: "Space Invite", message: response.error?.kind + ": " + response.error?.message})
              }
            } catch (e) {
              toast.add({ type: "danger", title: "Space Invite", message: `${e?.kind}: ${e?.message}`})
              console.error(e)
            }
          }
        } />
        <h3>Following users</h3>
        <FollowingUserList firebase={credential.user} user={user} />

        <h3>Follow users</h3>
        <FollowUserList firebase={credential.user} user={user} />

        <h3>Blocking users</h3>
        <BlockingUserList firebase={credential.user} user={user} />

        <h3>Block user form</h3>
        <BlockUserForm firebase={credential.user} />
      </div>

      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Space Invite</h3>
        <InviteForm
          onSubmitJoin={async (inviteKey) => {
            try {
              const idToken = await context.credential.user.getIdToken()
              const invite = await api.GET("/api/v2/invites/{invite_key}", {
                params: { path: { invite_key: inviteKey } },
                headers: {
                  authorization: `Bearer ${idToken}`,
                },
              })
              if (invite.data === undefined) {
                toast.add({ type: "danger", title: "Space Invite", message: "存在しない招待" })
                console.error(invite.error)
                return
              }
              if (invite.data.kind === "space") {
                await api.POST("/api/v2/spaces/{space_key}/users/me", {
                  params: { path: { space_key: invite.data.spaceKey } },
                  headers: {
                    authorization: `Bearer ${idToken}`,
                  },
                  body: { inviteKey: inviteKey },
                })
                toast.add({ type: "success", title: "Space Invite", message: `${invite.data.spaceKey}に参加しました` })
              } else {
                toast.add({ type: "danger", title: "Space Invite", message: "この招待コードはフレンド招待用です。" })
              }
            } catch (e) {
              console.error(`${e?.kind}: ${e?.message}`)
              console.error(e)
            }
          }}
          onSubmitCheck={async (inviteKey) => {
            try {
              const idToken = await context.credential.user.getIdToken()
              const response = await api.GET("/api/v2/invites/{invite_key}", {
                params: { path: { invite_key: inviteKey } },
                headers: {
                  authorization: `Bearer ${idToken}`,
                },
              })
              if (response.data) {
                toast.add({ type: "success", title: "Space Invite", message: "OK" })
              } else {
                toast.add({ type: "danger", title: "Space Invite", message: "存在しない招待" })
              }
            } catch (e) {
              toast.add({ type: "danger", title: "Space Invite", message: `${e?.kind}: ${e?.message}`})
              console.error(e)
            }
          }}
         />
      </div>

      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Push Devices</h3>
        <UserDeviceList firebase={credential.user} user={user} />
        <UserDeviceRegisterForm firebase={credential.user} />
      </div>

      <div className="col-12 col-lg-6 d-flex flex-column align-items-center gap-3">
        <h3>Push Settings</h3>
        {
          pushSettings === "loading" ? <Spinner /> :
            <UserPushSettingForm firebase={credential.user} currentSettings={pushSettings} />
        }
      </div>

      <div className="col-12 d-flex flex-column align-items-center gap-3 mt-5">
        {userDevices !== "loading" && (
          <UserDeviceDebug 
            firebase={credential.user} 
            user={user} 
            userDevices={userDevices}
          />
        )}
      </div>

      <div className="col-12 d-flex flex-column align-items-center gap-3 mt-5">
        <h3>Account Delete</h3>

        <button
          className="btn btn-danger form-control"
          onClick={async () => {
            try {
              await credential.user.getIdToken().then((idToken) =>
                api.DELETE("/api/v2/users", {
                  headers: {
                    authorization: `Bearer ${idToken}`,
                  },
                }),
              )
            } catch (e) {
              console.error(e)
            } finally {
              await FirebaseAuth.signOut(auth)
              window.location.reload()
            }
          }}
        >
          <i className="fas fa-user-minus me-2" />
          Call &quot;DELETE /users/me&quot; API
        </button>
      </div>
    </div>
  </>)
}
